import type { WalkthroughMatching } from './walkthroughSummary'
import React from 'react'
// NOTE: re-add Zap to this import when un-commenting the Optimizer button below.
import { Play, Send } from 'lucide-react'
import { Button, ChatInput, ChatMessageUpgrade } from '@/components'
import type { ChatInputHandle, UserMessageAttachment } from '@/components'
import ClarifyOverlay from './ClarifyOverlay'
import { CLARIFY_TOOL_NAME } from './clarifyTypes'
import type { ClarifyData } from './clarifyTypes'
import BotMessageUser from './BotMessageUser'
import BotMessageAssistant from './BotMessageAssistant'
import BotMessageToolForm from './BotMessageToolForm'
import BotMessageToolGenerateCode from './BotMessageToolGenerateCode'
import BotMessageToolBacktest from './BotMessageToolBacktest'
import BotMessageToolOptimizer from './BotMessageToolOptimizer'
import BotMessageToolWalkthrough from './BotMessageToolWalkthrough'
import CodeReadyPrompt, { type CodeReadyVariant } from './CodeReadyPrompt'
import BotMessageWorkbench from './BotMessageWorkbench'
import WorkbenchChangesPanel from './WorkbenchChangesPanel'
import WorkbenchComposerTab from './WorkbenchComposerTab'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus, ReplyOffer } from '../api'
import { WORKBENCH_MESSAGE_MAX, type WorkbenchUpdateSummary } from '../workbenchUpdate'

/** What an upgrade card's button should do. */
export type UpgradeAction = 'billing' | 'paywall'

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'upgrade' | 'workbench'
  content: string
  /** role 'workbench': the update Send to AI sent, as its card shows it. */
  workbench?: WorkbenchUpdateSummary
  /**
   * Assistant turns run with reasoning on: the model's summary of its reasoning.
   * Arrives before `content` while streaming, so a message can legitimately have
   * reasoning and no content yet.
   */
  reasoning?: string
  /** Assistant replies that end by asking whether to generate the code: answered by a button. */
  offer?: ReplyOffer
  timestamp: Date
  status?: MessageStatus
  streaming?: boolean
  /** Upgrade cards only: overrides the default CTA label and destination. */
  upgradeAction?: UpgradeAction
  ctaLabel?: string
  attachments?: UserMessageAttachment[]
  messageType?: 'content' | 'tool'
  toolData?: {
    toolName: string
    toolInput: Record<string, unknown>
    startedAt: string
    completedAt?: string
    status: 'running' | 'completed'
    componentType?: string
    componentData?: Record<string, unknown>
    responses?: Record<string, string | string[]>
    dismissed?: boolean
    toolStatus?: string
    config?: Record<string, any>
    btid?: string
    cid?: string
    optid?: string
    version?: number
    codeVersion?: number
    codeId?: number
    /** run_walkthrough: which automatic verification round this run was. */
    round?: number
    maxRounds?: number
    /** run_walkthrough: false when the user asked for the run rather than the loop. */
    autoRun?: boolean
    /** run_walkthrough: the app's verdict on the run. */
    assessment?: { level?: string; cause?: string | null }
    walkthrough?: {
      symbol?: string
      timeframe?: string
      period?: { startDate?: string; endDate?: string } | null
    }
    /** run_walkthrough: how the code's orders lined up with the drawn ideas. */
    matching?: WalkthroughMatching
    /** run_walkthrough: the AI accepted this run as reproducing the drawings. */
    verified?: boolean
    error?: string
  }
  error?: string
}

interface BotChatProps {
  messages: Message[]
  /**
   * `intent` marks a send that exists only to start a run, so the caller can
   * refuse it up front on a plan that cannot run them — no chat turn spent.
   */
  onSendMessage: (value: string, opts?: { intent?: 'backtest' | 'optimizer' }) => void
  onComponentSubmit: (msgId: string, data: Record<string, unknown>) => void
  onComponentDismiss: (msgId: string) => void
  onCancelMessage?: () => void
  onRetryMessage?: () => void
  onUpgradeClick?: (action?: UpgradeAction) => void
  /** "Run optimizer" on a completed-backtest badge. */
  onRunOptimizer?: () => void
  /** "View full report" on a completed-backtest badge — opens the Backtest tab. */
  onViewReport?: () => void
  /** The next step offered under the conversation, or null for no offer: a just-generated
   *  strategy's walkthrough or backtest, or the answer to the AI asking whether to generate
   *  the code. The caller owns both WHETHER it is on and WHICH it is — see BotDetail. */
  codeCta?: CodeReadyVariant | null
  /** "Run walkthrough test" — enters the Create tab's walkthrough mode. */
  onRunWalkthrough?: () => void
  /** "View walkthrough" on a finished verification card. Falls back to onRunWalkthrough:
   *  both lead to the same mode, the run is simply already built. */
  onViewWalkthrough?: () => void
  /** "Run a backtest" on the no-drawings variant of the same offer. */
  onRunBacktest?: () => void
  /** "Generate code" / "Regenerate code" — says yes to the AI's offer to write the code. */
  onGenerateCode?: () => void
  /** "View code" on either variant — opens the Backtest tab's Code panel. */
  onViewCode?: () => void
  isLoading?: boolean
  hasCode?: boolean
  hideHeader?: boolean
  isMobileLayout?: boolean
  inputTrailingSlot?: React.ReactNode
  /** Reasoning-effort control for the composer chrome row. */
  effortSlot?: React.ReactNode
  /** Far-left chrome-row control (the Auto-verify switch). */
  chromeStartSlot?: React.ReactNode
  /** Show the "Run Backtest" quick action. */
  showBacktestAction?: boolean
  /**
   * Workbench changes waiting for the AI. While `pending` is set the composer
   * wears a "Chart updated" tab: what changed, then the box for an optional
   * message, sent together by Send to AI. Omit, or pending null, for the plain
   * composer (no tab).
   */
  workbench?: {
    pending: WorkbenchUpdateSummary | null
    /**
     * `message` is what was typed with the changes — '' when nothing was. May resolve
     * false when nothing was sent, which puts the typed text back in the box.
     */
    onSend: (message: string) => void | Promise<boolean | void>
  }
}

export default function BotChat({
  messages,
  onSendMessage,
  onComponentSubmit,
  onComponentDismiss,
  onCancelMessage,
  onRetryMessage,
  onUpgradeClick,
  onRunOptimizer,
  onViewReport,
  codeCta = null,
  onRunWalkthrough,
  onViewWalkthrough,
  onRunBacktest,
  onGenerateCode,
  onViewCode,
  isLoading = false,
  hasCode = false,
  hideHeader = false,
  isMobileLayout = false,
  inputTrailingSlot,
  effortSlot,
  chromeStartSlot,
  showBacktestAction = false,
  workbench,
}: BotChatProps) {
  const messagesContainerRef = React.useRef<HTMLDivElement>(null)
  const feedRef = React.useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = React.useRef(0)
  const hasScrolledOnLoadRef = React.useRef(false)
  const [showScrollButton, setShowScrollButton] = React.useState(false)
  const chatInputRef = React.useRef<ChatInputHandle>(null)
  // Check if there's any in_progress message (blocks input)
  const hasInProgressMessage = React.useMemo(() => {
    return messages.some(m => m.status === MESSAGE_STATUS.IN_PROGRESS)
  }, [messages])

  // Check if there's a failed message (blocks input until resolved)
  const failedMessage = React.useMemo(() => {
    return messages.find(m => m.status === MESSAGE_STATUS.FAILED)
  }, [messages])

  // Pending clarifying-questions form: the stepper overlay replaces the
  // composer while it's unanswered. Keyed off message.status (not
  // toolData.status, which is 'running' via SSE but 'waiting_user' from DB
  // load). The questions guard keeps the plain composer during the instant
  // between tool-started and type-changed when componentData isn't set yet.
  const pendingQuestionsMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (
        m.status === MESSAGE_STATUS.IN_PROGRESS &&
        m.toolData?.toolName === CLARIFY_TOOL_NAME &&
        ((m.toolData.componentData as ClarifyData | undefined)?.questions?.length ?? 0) > 0
      ) {
        return m
      }
    }
    return null
  }, [messages])

  // Check if there's an active tool call (to suppress assistant spinner)
  const hasActiveToolCall = React.useMemo(() => {
    return messages.some(m => m.messageType === 'tool' && m.toolData && m.toolData.status === 'running')
  }, [messages])

  // "View walkthrough" belongs on ONE card: the latest finished run, and only once the
  // chat is idle. The button opens walkthrough mode for the CURRENT code, so on an older
  // round's card it would open a run other than the one the card describes; and while the
  // loop is still going, the run on screen may be about to be superseded.
  const latestWalkthroughId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.toolData?.toolName === 'run_walkthrough' && m.toolData.status === 'completed' && m.toolData.btid) {
        return m.id
      }
    }
    return null
  }, [messages])

  // Input should be disabled if: loading, in_progress message, or failed message
  const isInputDisabled = isLoading || hasInProgressMessage || !!failedMessage

  // Workbench changes waiting: the composer becomes Send to AI — the changes, plus
  // whatever is typed in the box below them. The draft is the same one either way,
  // so changes landing (or going away) mid-sentence keep what was typed.
  const pendingWorkbench = workbench?.pending ?? null

  // Scroll to bottom on initial load if there are messages
  React.useEffect(() => {
    if (!hasScrolledOnLoadRef.current && messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      hasScrolledOnLoadRef.current = true
    }
  }, [messages])

  // Scroll when new messages are appended: reveal the beginning of the new
  // content if the user was at the bottom (or sent a message themselves);
  // leave the viewport alone when they are reading older messages. Streaming
  // chunks don't grow the count, so a long reply never drags the view down
  // while it streams in. Several messages can land in one render (the server
  // emits message-user and message-started back-to-back), so the whole
  // appended batch is considered, anchored on its first message.
  React.useEffect(() => {
    const prevLength = prevMessagesLengthRef.current
    prevMessagesLengthRef.current = messages.length
    // prevLength === 0 is the initial population (or a bot switch) — the
    // load effect above owns positioning there.
    if (prevLength === 0 || messages.length <= prevLength) return

    const isOwnSend = messages.slice(prevLength).some((m) => m.role === 'user' || m.role === 'workbench')
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current
      const wrappers = feedRef.current?.children
      const firstNew = wrappers?.[prevLength] as HTMLElement | undefined
      if (!container || !wrappers || !firstNew) return

      // The DOM already contains the appended batch — discount what it added
      // (heights + the space-y-4 gaps) to know where the user stood before
      // it landed. 100px slack mirrors handleScroll's isAtBottom.
      let addedHeight = 0
      for (let i = prevLength; i < wrappers.length; i++) {
        addedHeight += (wrappers[i] as HTMLElement).offsetHeight + 16
      }
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight
      const wasNearBottom = distanceFromBottom - addedHeight < 100

      if (isOwnSend || wasNearBottom) {
        // offsetTop is relative to the container (its offsetParent): tall new
        // content gets its beginning aligned to the top; short content clamps
        // to a plain scroll-to-bottom.
        container.scrollTo({
          top: Math.max(0, firstNew.offsetTop - 8),
          behavior: 'smooth',
        })
      }
    })
  }, [messages])

  // Reveal scroll for the next-step prompt. It appears when the last message merely FINISHES
  // streaming — nothing is appended — so the append-based autoscroll above never fires and the
  // prompt can land under the composer. Same fix as the backtest results badge, and the same
  // exclusion: an initial history load that arrives with the prompt already on must not be
  // yanked, so this only scrolls when the prompt turns on over a feed that was ALREADY
  // populated. Its own ref rather than prevMessagesLengthRef, which the effect above has
  // already advanced by the time this one runs.
  const ctaRef = React.useRef<HTMLDivElement>(null)
  const ctaRevealRef = React.useRef<{ cta: CodeReadyVariant | null; hadMessages: boolean }>({
    cta: null,
    hadMessages: false,
  })
  React.useEffect(() => {
    const prev = ctaRevealRef.current
    ctaRevealRef.current = { cta: codeCta, hadMessages: messages.length > 0 }
    if (!codeCta || codeCta === prev.cta || !prev.hadMessages) return
    ctaRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [codeCta, messages.length])

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
      setShowScrollButton(!isAtBottom)
    }
  }

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  const placeholder = failedMessage
    ? 'Resolve the error above to continue...'
    : hasInProgressMessage
      ? 'Waiting for response...'
      : pendingWorkbench
        ? `Add a message to go with your ${pendingWorkbench.first ? 'chart' : 'changes'} (optional)…`
        : 'Type a message...'

  const primaryAction: 'send' | 'stop' =
    hasInProgressMessage && onCancelMessage ? 'stop' : 'send'

  // Banner row above the input: the "run a backtest" quick action, enabled
  // once nothing is running AND strategy code exists.
  const quickActionsDisabled = isInputDisabled || !hasCode
  const composerBanner =
    showBacktestAction ? (
      <div className="flex items-center gap-1.5 pb-1.5">
        {showBacktestAction && (
          <Button
            type="button"
            variant={quickActionsDisabled ? 'outline' : 'default'}
            size="xs"
            disabled={quickActionsDisabled}
            onClick={() => onSendMessage('run a backtest', { intent: 'backtest' })}
            title={!hasCode ? 'Generate strategy code first' : undefined}
          >
            <Play /> Run Backtest
          </Button>
        )}
        {/* <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={quickActionsDisabled}
          onClick={() => onSendMessage('run the parameter optimizer')}
          title={!hasCode ? 'Generate strategy code first' : undefined}
        >
          <Zap /> Optimizer
        </Button> */}
      </div>
    ) : undefined

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-background">
      {!hideHeader && (
        <div className="shrink-0 h-11 flex items-center px-4 border-b border-border bg-card">
          <div className="flex items-center gap-1.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-foreground shrink-0"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p className="text-label text-foreground">Chat</p>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 relative">
        <div
          ref={messagesContainerRef}
          className="absolute inset-0 overflow-y-auto overflow-x-hidden p-4"
          onScroll={handleScroll}
        >
          {messages.length === 0 ? (
            // The hand-off prompt from the very first visit: a new strategy opens
            // on the chart, so the one thing worth saying is that marking it up is
            // only half the job. Static — nothing here changes while it is up.
            <div className="mx-auto mt-8 max-w-[46ch] px-4 py-3 text-center leading-relaxed text-muted-foreground">
              I&apos;m watching what you&apos;re doing on the chart. When
              you&apos;re done, send your chart to me below and I&apos;ll automate
              your strategy. The more details you provide the better...
            </div>
          ) : (
            <div ref={feedRef} className="space-y-4">
              {messages.map((message) => {
                return (
                  <div key={message.id}>
                    {message.role === 'upgrade' ? (
                      <ChatMessageUpgrade
                        message={{ id: 0, type: 'upgrade', content: message.content }}
                        ctaLabel={message.ctaLabel ?? 'Check credit usage'}
                        onUpgrade={() => onUpgradeClick?.(message.upgradeAction)}
                      />
                    ) : message.role === 'workbench' && message.workbench ? (
                      <BotMessageWorkbench summary={message.workbench} />
                    ) : message.role === 'user' ? (
                      <BotMessageUser
                        content={message.content}
                        status={message.status}
                        attachments={message.attachments}
                      />
                    ) : message.role === 'assistant' ? (
                      message.messageType === 'tool' || message.toolData ? (
                        message.toolData?.toolName === CLARIFY_TOOL_NAME ? (
                          <BotMessageToolForm
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : message.toolData?.toolName === 'generate_trading_code' ? (
                          <BotMessageToolGenerateCode
                            toolData={message.toolData}
                            status={message.status}
                            msgId={message.id}
                          />
                        ) : message.toolData?.toolName === 'run_backtest' ? (
                          <BotMessageToolBacktest
                            toolData={message.toolData}
                            status={message.status}
                            msgId={message.id}
                            onRunOptimizer={onRunOptimizer}
                            onViewReport={onViewReport}
                            actionsDisabled={isInputDisabled}
                          />
                        ) : message.toolData?.toolName === 'run_walkthrough' ? (
                          <BotMessageToolWalkthrough
                            toolData={message.toolData}
                            status={message.status}
                            msgId={message.id}
                            onViewWalkthrough={onViewWalkthrough}
                            // Once, at the end — and not when the prose offer under the reply
                            // is already making the same button.
                            showViewButton={
                              message.id === latestWalkthroughId &&
                              !hasInProgressMessage &&
                              !isLoading &&
                              codeCta !== 'walkthroughReady' &&
                              !!onViewWalkthrough
                            }
                          />
                        ) : message.toolData?.toolName === 'run_optimizer' ? (
                          <BotMessageToolOptimizer
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : null
                      ) : (
                        <BotMessageAssistant
                          content={message.content}
                          reasoning={message.reasoning}
                          status={message.status}
                          streaming={message.streaming}
                          error={message.error}
                          onRetry={onRetryMessage}
                          onCancel={onCancelMessage}
                          hasActiveToolCall={hasActiveToolCall}
                        />
                      )
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}

          {/* Deliberately a SIBLING of the feed rather than a child of it: the autoscroll effect
              indexes feedRef.current.children against messages.length, and an extra child would
              be read as an appended message. `mt-4` stands in for the space-y-4 it forgoes. */}
          {codeCta && (
            <div ref={ctaRef} className="mt-4">
              <CodeReadyPrompt
                variant={codeCta}
                onPrimary={
                  codeCta === 'backtest'
                    ? onRunBacktest
                    : codeCta === 'generateCode' || codeCta === 'regenerateCode'
                      ? onGenerateCode
                      : onRunWalkthrough
                }
                onViewCode={onViewCode}
                actionsDisabled={isInputDisabled}
              />
            </div>
          )}
        </div>

        {showScrollButton && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 px-3 py-1.5 rounded-full text-caption bg-card text-foreground border border-border shadow-md"
            aria-label="Scroll to bottom"
          >
            ↓
          </button>
        )}
      </div>

      <ChatInput
        ref={chatInputRef}
        id="chat-composer"
        onSubmit={(text) => (pendingWorkbench ? workbench?.onSend(text) : onSendMessage(text))}
        onStop={onCancelMessage ? () => onCancelMessage() : undefined}
        primaryAction={primaryAction}
        disabled={isInputDisabled && primaryAction !== 'stop'}
        placeholder={placeholder}
        isMobileLayout={isMobileLayout}
        bannerSlot={
          composerBanner || pendingWorkbench ? (
            <>
              {composerBanner}
              {pendingWorkbench && <WorkbenchComposerTab first={pendingWorkbench.first} />}
            </>
          ) : undefined
        }
        tone={pendingWorkbench ? 'warning' : 'default'}
        // The tab sits flush on the card's left edge: square that corner so the two
        // borders run as one straight line.
        cardClassName={pendingWorkbench ? 'rounded-tl-none' : undefined}
        headerSlot={pendingWorkbench ? <WorkbenchChangesPanel summary={pendingWorkbench} /> : undefined}
        sendLabel={
          pendingWorkbench ? (
            <>
              <Send className="size-3.5" /> Send to AI
            </>
          ) : undefined
        }
        // The message is optional: the changes alone are worth sending.
        allowEmptySubmit={!!pendingWorkbench}
        maxLength={pendingWorkbench ? WORKBENCH_MESSAGE_MAX : undefined}
        trailingChromeSlot={inputTrailingSlot}
        leadingChromeSlot={effortSlot}
        chromeStartSlot={chromeStartSlot}
        overlaySlot={
          pendingQuestionsMessage?.toolData ? (
            <ClarifyOverlay
              // Keyed by message id so a new questions form resets the stepper
              key={pendingQuestionsMessage.id}
              msgId={pendingQuestionsMessage.id}
              data={pendingQuestionsMessage.toolData.componentData as unknown as ClarifyData}
              onSubmit={onComponentSubmit}
              onDismiss={onComponentDismiss}
            />
          ) : undefined
        }
      />
    </div>
  )
}

export type { BotChatProps }
